import { type ReactElement, type ReactNode, useEffect } from "react"
import { EndUserInstanceType } from "../../types/EndUserInstanceType"
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import Modal from "src/components/Modal";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "src/components/Button";
import { ResetAdminPasswordType } from "../../types/ResetAdminPasswordType";

export interface ResetAdminPasswordModalProps {
        title: string,
        isOpen: boolean,
        resetAdminPassword: ResetAdminPasswordType | undefined,
        onClose: () => void,
        onSubmit: (value: ResetAdminPasswordType) => void,
        children?: ReactNode
}

export default function ResetAdminPasswordModal(props: ResetAdminPasswordModalProps): ReactElement {
        const { register, watch, handleSubmit, reset, clearErrors, setError, formState: { errors, isSubmitting } } = useForm<ResetAdminPasswordType>({
                defaultValues: props.resetAdminPassword
        });

        const { authUser } = useSelector((state: RootState) => state.auth);
        const { endUser } = useSelector((state: RootState) => state.endUser);
        const { endUserInstance, endUserInstanceError } = useSelector((state: RootState) => state.endUserInstance);

        const onSubmit: SubmitHandler<ResetAdminPasswordType> = (data) => {
                props.onSubmit(data);
                reset();
        }

        useEffect(() => {
                let error = "";
                reset(props.resetAdminPassword);
                if (endUserInstanceError !== null) {
                        error = endUserInstanceError;
                }
                setError("root", {
                        type: "manual",
                        message: error,
                });
        }, [endUserInstanceError])

        const authorized = authUser?.UserType === "admin" ? false : true;

        return <>
                <Modal isOpen={props.isOpen} modalSize="lg" onClose={props.onClose}>
                        <h2 className="text-lg font-bold text-center">
                                {props.title}
                        </h2>
                        <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
                                <div className="flex justify-center">
                                        <div className="flex flex-col">
                                                <div className="mt-4">
                                                        <label className="text-gray-800 text-[15px] mb-2 block">Password</label>
                                                        <div className="relative flex items-center">
                                                                <input type="password" className="w-full text-sm text-gray-800 bg-gray-100 focus:bg-transparent pl-4 pr-10 py-3.5 rounded-xs outline-blue-600" autoComplete="new-password" placeholder="Enter password" {...register("Password", { required: "Password is required", minLength: { value: 8, message: "Password must be atleast 8 characters" }, })} />
                                                        </div>
                                                        {errors.Password && (
                                                                <div className="text-red-500">{errors.Password.message}</div>
                                                        )}
                                                </div>
                                                <div className="mt-4">
                                                        <label className="text-gray-800 text-[15px] mb-2 block">Confirm Password</label>
                                                        <div className="relative flex items-center">
                                                                <input type="password" className="w-full text-sm text-gray-800 bg-gray-100 focus:bg-transparent pl-4 pr-10 py-3.5 rounded-xs outline-blue-600" autoComplete="new-password" placeholder="Confirm password" {...register("ConfirmPassword", {
                                                                        required: "Confirm Password is required", validate: (value: string) => {
                                                                                if (watch('Password') !== watch('ConfirmPassword')) {
                                                                                        return "The Passwords do not match";
                                                                                }
                                                                        },
                                                                })} />
                                                        </div>
                                                        {errors.ConfirmPassword && (
                                                                <div className="text-red-500">{errors.ConfirmPassword.message}</div>
                                                        )}
                                                </div>
                                        </div>
                                </div>
                                <div className="flex gap-16 justify-around">
                                        <Button className="" size="lg" type="reset" buttonStyle="delete">Reset</Button>
                                        <Button className="" size="lg" type="submit" buttonStyle="primary">Save</Button>
                                </div>
                        </form>
                        {errors.root && (
                                <div className="text-red-500 text-center">{errors.root.message}</div>
                        )}
                </Modal>
        </>
}
